/**
 * The trend-data chart used by the single-animal trend tab (`src/components/AnimalTrendData`,
 * `/animal/:id`), the animal-search trend view (`src/pages/AnimalSearch`), and the multi-series
 * comparisons (`src/pages/AnimalCompareSearch`, `src/pages/AnimalReports`, `src/components/ScheduleReportModal`).
 *
 * Mirrors `HealthSystemDomainAnimalsResultAnimalTrendDataViewModel`, returned by
 * `animalsGetAnimalTrendData`, `animalsGetSearchAnimalsTrendData` and - keyed by search index, as a
 * `Record<string, ...>` - `animalsGetCompareSearchAnimalsTrendData`. `slaughterhouseDataAverage` and
 * `weightAverage` reuse `AnimalSlaughterhouseData`/`AnimalWeight` from `./animal-view` rather than
 * duplicating them.
 */
import {
  HealthSystemDomainAnimalsResultAnimalBirthCycleViewModel,
  HealthSystemDomainAnimalsResultAnimalTrendDataViewModel,
  HealthSystemDomainAnimalsResultTrendDataInfo
} from '../api/HealthSystemApi';
import { AnimalSlaughterhouseData, AnimalWeight, toAnimalSlaughterhouseData, toAnimalWeight } from './animal-view';

/**
 * A birth cycle option as offered by `BirthCycleSelector`'s dropdown. Trimmed to `dateOfBirth`
 * and `birthCycleNumber`, the only fields the selector reads.
 */
export interface BirthCycleOption {
  dateOfBirth: string;
  birthCycleNumber: number;
}

export const toBirthCycleOption = (
  dto: HealthSystemDomainAnimalsResultAnimalBirthCycleViewModel
): BirthCycleOption => ({
  dateOfBirth: dto.dateOfBirth,
  birthCycleNumber: dto.birthCycleNumber
});

/** One bar/point in the trend chart - one per distinct value of the selected trend type. */
export interface AnimalTrendDataPoint {
  animalCount: number;
  name: string | null;
  /** Available values: Grams, Millimeters, Kilograms, Percentage, MoneyEuro. */
  unit: string | null;
  value: number;
}

export const toAnimalTrendDataPoint = (dto: HealthSystemDomainAnimalsResultTrendDataInfo): AnimalTrendDataPoint => ({
  animalCount: dto.animalCount,
  name: dto.name ?? null,
  unit: dto.unit ?? null,
  value: dto.value
});

export interface AnimalTrendData {
  trendData: AnimalTrendDataPoint[];
  /** Null when there is no slaughterhouse feedback to average over the matched animals. */
  slaughterhouseDataAverage: AnimalSlaughterhouseData | null;
  /** Null when there is no weight history to average over the matched animals. */
  weightAverage: AnimalWeight | null;
}

export const toAnimalTrendData = (dto: HealthSystemDomainAnimalsResultAnimalTrendDataViewModel): AnimalTrendData => ({
  trendData: (dto.trendData ?? []).map(toAnimalTrendDataPoint),
  slaughterhouseDataAverage: dto.slaughterhouseDataAverage
    ? toAnimalSlaughterhouseData(dto.slaughterhouseDataAverage)
    : null,
  weightAverage: dto.weightAverage ? toAnimalWeight(dto.weightAverage) : null
});

/** The compare and report endpoints return one trend per search, keyed by the search's index. */
export const toAnimalTrendDataByKey = (
  dtos: Record<string, HealthSystemDomainAnimalsResultAnimalTrendDataViewModel>
): Record<string, AnimalTrendData> =>
  Object.fromEntries(Object.entries(dtos).map(([key, dto]) => [key, toAnimalTrendData(dto)]));
